import { motion } from 'framer-motion';
import { Inbox, Package, TrendingUp, IndianRupee, ArrowUpRight } from 'lucide-react';
import api from '../lib/api';
import useAsync from '../hooks/useAsync';
import Loader, { ErrorState } from '../components/Loader';
import { formatINR } from '../lib/whatsapp';
import { statusColors } from './sampleInquiries';

const STATUSES = ['New', 'Contacted', 'Quoted', 'Closed'];

async function loadDashboard() {
  const [statsRes, pkgRes] = await Promise.all([
    api.get('/inquiries/stats'),
    api.get('/packages?all=true'),
  ]);
  return { stats: statsRes.data, packageCount: pkgRes.data.length };
}

export default function Dashboard() {
  const { data, loading, error, reload } = useAsync(loadDashboard, []);

  if (loading) return <Loader label="Loading dashboard…" />;
  if (error) return <ErrorState message="Couldn't load dashboard data." onRetry={reload} />;

  const { stats, packageCount } = data;
  const byStatus = stats.byStatus || {};
  const closed = byStatus.Closed || 0;
  const conversion = stats.total ? Math.round((closed / stats.total) * 100) : 0;
  const maxStatus = Math.max(1, ...STATUSES.map((s) => byStatus[s] || 0));

  const cards = [
    { label: 'Total Inquiries', value: stats.total, icon: Inbox },
    { label: 'Active Packages', value: packageCount, icon: Package },
    { label: 'Conversion', value: `${conversion}%`, icon: TrendingUp },
    { label: 'Pipeline Value', value: formatINR(stats.pipelineValue || 0), icon: IndianRupee },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-display text-2xl font-bold">Dashboard</h1>
        <p className="text-sm text-ink-400">Live overview of inquiries, packages and performance.</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map((c, i) => (
          <motion.div
            key={c.label}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.07 }}
            className="glass p-5"
          >
            <div className="flex items-center justify-between">
              <span className="grid h-11 w-11 place-items-center rounded-xl bg-brand-gradient text-white">
                <c.icon size={20} />
              </span>
              <span className="flex items-center gap-1 text-xs text-success">
                <ArrowUpRight size={13} /> live
              </span>
            </div>
            <p className="mt-4 font-display text-2xl font-bold text-white">{c.value}</p>
            <p className="text-sm text-ink-400">{c.label}</p>
          </motion.div>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Status breakdown */}
        <div className="glass p-6">
          <h2 className="mb-5 font-display font-semibold">Inquiries by Status</h2>
          <div className="space-y-3">
            {STATUSES.map((s, i) => {
              const count = byStatus[s] || 0;
              return (
                <div key={s}>
                  <div className="mb-1 flex justify-between text-xs text-ink-300">
                    <span>{s}</span><span>{count}</span>
                  </div>
                  <div className="h-2.5 overflow-hidden rounded-pill bg-white/5">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${(count / maxStatus) * 100}%` }}
                      transition={{ delay: i * 0.08, duration: 0.6 }}
                      className="h-full rounded-pill bg-brand-gradient"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Recent inquiries */}
        <div className="glass p-6">
          <h2 className="mb-5 font-display font-semibold">Recent Inquiries</h2>
          {(!stats.recent || stats.recent.length === 0) ? (
            <p className="py-8 text-center text-sm text-ink-400">No inquiries yet.</p>
          ) : (
            <ul className="divide-y divide-white/5">
              {stats.recent.map((q) => (
                <li key={q._id} className="flex items-center justify-between py-3">
                  <div>
                    <p className="text-sm font-medium text-white">{q.name}</p>
                    <p className="text-xs text-ink-400">
                      {q.package?.title || 'General'} · {q.eventDate || '—'}
                    </p>
                  </div>
                  <span className={`rounded-pill px-2.5 py-1 text-xs font-medium ${statusColors[q.status] || ''}`}>
                    {q.status}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
